import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EASY_MODE_PARTNERS } from '../constants/easyModeIntegrations';
import { getPartnerConfig } from '../utils/enum';
import Constants from '../Constants';
import useAxios from './useAxios';
import usePartnerCaseList from './usePartnerCaseList';

// The import runs against the same PFQ endpoints Classic Intake uses, so the wizard inherits every
// partner that has a strategy. The difference is where it lands: Classic navigates to the case it
// just filled, and the wizard stays on page 2 and refetches, because the user is still in it.
export const getPartnerLabel = ({ partner }) =>
  EASY_MODE_PARTNERS.find((candidate) => candidate.value === partner)?.text ?? partner;

const useEasyModePartnerImport = ({ caseId, onImported }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [isAutoImporting, setIsAutoImporting] = useState(false);
  const [importedPartnerLabel, setImportedPartnerLabel] = useState('');
  const [partnerApiKeysList, setPartnerApiKeysList] = useState([]);

  const partnerCases = usePartnerCaseList({ partner: selectedPartner || null, caseId });
  const { reset: resetPartnerCases, directImport } = partnerCases;

  // Same gate Classic Intake uses: only a partner the firm holds an API key for can be imported from,
  // so a stored key is what makes its button appear.
  const apiConfig = useMemo(() => ({ timeout: 60000 }), []);
  const { getData } = useAxios(apiConfig);
  const calledRef = useRef(false);
  const fetchPartnerApiKeys = useCallback(async () => {
    if (calledRef.current) return;
    calledRef.current = true;
    const { success, data } = await getData(Constants.ApiUrl.user.getPartnerApiKeys);
    if (success) setPartnerApiKeysList(data || []);
  }, [getData]);

  useEffect(() => {
    fetchPartnerApiKeys();
  }, [fetchPartnerApiKeys]);

  // a partner is offered only when a stored key resolves to a config we still ship a button for
  const availablePartners = useMemo(() => {
    const configuredKeys = new Set(
      partnerApiKeysList
        .map((partnerItem) => getPartnerConfig(partnerItem.partnerId)?.key)
        .filter(Boolean)
    );

    return EASY_MODE_PARTNERS.filter((partner) => configuredKeys.has(partner.value));
  }, [partnerApiKeysList]);

  const toggleExpanded = () => setIsExpanded((previous) => !previous);

  const selectPartner = useCallback(({ partner }) => {
    setSelectedPartner(partner);
    setSelectedCase(null);
    setIsAutoImporting(false);
    resetPartnerCases();
  }, [resetPartnerCases]);

  const clearPartner = useCallback(() => selectPartner({ partner: '' }), [selectPartner]);

  const selectCase = ({ partnerCase }) => setSelectedCase(partnerCase);

  const closeConfirmation = useCallback(() => {
    setSelectedCase(null);
    setIsAutoImporting(false);
  }, []);

  // the backend matched this case by id and started importing without being asked, so the
  // confirmation opens already in flight
  useEffect(() => {
    if (!directImport) return;

    setSelectedCase({ id: directImport.partnerCaseId, caseName: directImport.caseName });
    setIsAutoImporting(true);
  }, [directImport]);

  const handleImported = useCallback(async ({ partner }) => {
    setImportedPartnerLabel(getPartnerLabel({ partner }));
    setSelectedPartner('');
    setSelectedCase(null);
    setIsExpanded(false);
    await onImported();
  }, [onImported]);

  const dismissBanner = () => setImportedPartnerLabel('');

  return {
    isExpanded,
    availablePartners,
    selectedPartner,
    partnerLabel: selectedPartner ? getPartnerLabel({ partner: selectedPartner }) : '',
    selectedCase,
    isAutoImporting,
    importedPartnerLabel,
    partnerCases,
    toggleExpanded,
    selectPartner,
    clearPartner,
    selectCase,
    closeConfirmation,
    handleImported,
    dismissBanner
  };
};

export default useEasyModePartnerImport;
